"use client"

import * as React from "react"
import { useNavigate } from "react-router"
import { NumericCell } from "@/components/data-views"
import { HUB_TABLE_EMBEDDED_PREVIEW_ROW_LIMIT } from "@/components/data-views"
import { DataTable } from "@/components/data-table"
import { TableViewMoreFooter } from "@exxatdesignux/ui/components/data-table/table-view-more-footer"
import type { ColumnDef } from "@/components/data-table/types"
import { formatTermDateRange, type TermTimelinePosition } from "@/lib/evaluation-term-utils"
import {
  evaluationCoveragePercent,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import { useProduct } from "@/contexts/product-context"
import {
  TermCoverageCell,
  TermIdentityCell,
  TermPublishStatusCell,
  TermRatingCell,
  TermResponseCell,
  TermRowActionsCell,
  TermTimelineStatusCell,
  EVAL_TABLE_ACTION_COLUMN_MULTI,
} from "./evaluation-term-table-cells"

type AllTermRow = EvaluationTerm & Record<string, unknown>

const TIMELINE_SORT: Record<TermTimelinePosition, number> = {
  current: 0,
  last: 1,
  upcoming: 2,
  future: 3,
  past: 4,
}

function buildColumns(
  product: ReturnType<typeof useProduct>["product"],
  onNavigate: (path: string) => void,
): ColumnDef<AllTermRow>[] {
  return [
    {
      key: "timeline",
      label: "Timeline",
      width: 120,
      minWidth: 108,
      sortable: true,
      sortKey: "timelineSort",
      cellKind: "status",
      cell: row => <TermTimelineStatusCell term={row} />,
    },
    {
      key: "term",
      label: "Term",
      width: 220,
      minWidth: 180,
      sortable: true,
      sortKey: "termName",
      defaultPin: "left",
      cellKind: "text",
      cell: row => <TermIdentityCell term={row} />,
    },
    {
      key: "dates",
      label: "Dates",
      width: 200,
      minWidth: 160,
      sortable: true,
      sortKey: "startDate",
      cellKind: "date",
      cell: row => (
        <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
          {formatTermDateRange(row.startDate, row.endDate)}
        </span>
      ),
    },
    {
      key: "courses",
      label: "Courses",
      width: 96,
      minWidth: 80,
      sortable: true,
      sortKey: "courseOfferings",
      cellKind: "numeric",
      cell: row => <NumericCell value={row.courseOfferings} />,
    },
    {
      key: "coverage",
      label: "Coverage",
      width: 168,
      minWidth: 150,
      sortable: true,
      sortKey: "coveragePercent",
      cellKind: "progress",
      cell: row => <TermCoverageCell term={row} />,
    },
    {
      key: "response",
      label: "Response",
      width: 176,
      minWidth: 164,
      sortable: true,
      sortKey: "responseRate",
      cellKind: "progress",
      cell: row => <TermResponseCell term={row} />,
    },
    {
      key: "courseAvg",
      label: "Course avg",
      width: 112,
      minWidth: 96,
      sortable: true,
      sortKey: "courseAverage",
      cellKind: "numeric",
      cell: row => <TermRatingCell value={row.ratings?.courseAverage} />,
    },
    {
      key: "facultyAvg",
      label: "Faculty avg",
      width: 112,
      minWidth: 96,
      sortable: true,
      sortKey: "facultyAverage",
      cellKind: "numeric",
      cell: row => <TermRatingCell value={row.ratings?.facultyAverage} />,
    },
    {
      key: "stage",
      label: "Evaluation stage",
      width: 160,
      minWidth: 140,
      sortable: true,
      sortKey: "stageLabel",
      cellKind: "status",
      cell: row => <TermPublishStatusCell term={row} />,
    },
    {
      key: "action",
      label: "Action",
      ...EVAL_TABLE_ACTION_COLUMN_MULTI,
      cell: row => (
        <TermRowActionsCell term={row} product={product} onNavigate={onNavigate} />
      ),
    },
  ]
}

function toSortableRows(terms: EvaluationTerm[]): AllTermRow[] {
  return terms.map(term => ({
    ...term,
    timelineSort: TIMELINE_SORT[term.timelinePosition],
    coveragePercent: evaluationCoveragePercent(term.counts, term.courseOfferings),
    responseRate:
      term.timelinePosition === "current" && term.liveResponseRate != null
        ? term.liveResponseRate
        : (term.closedResponseRate ?? 0),
    stageLabel: term.cardCase,
    courseAverage: term.ratings?.courseAverage ?? 0,
    facultyAverage: term.ratings?.facultyAverage ?? 0,
  }))
}

export function AllTermsTable({
  terms,
  embeddedPreview,
  embeddedPreviewRowLimit = HUB_TABLE_EMBEDDED_PREVIEW_ROW_LIMIT,
  onEmbeddedPreviewViewMore,
  embeddedPreviewViewMoreLabel,
}: {
  terms: EvaluationTerm[]
  embeddedPreview?: boolean
  embeddedPreviewRowLimit?: number
  onEmbeddedPreviewViewMore?: () => void
  embeddedPreviewViewMoreLabel?: string
}) {
  const { product } = useProduct()
  const navigate = useNavigate()
  const [embeddedExpanded, setEmbeddedExpanded] = React.useState(false)
  const onNavigate = React.useCallback((path: string) => navigate(path), [navigate])
  const columns = React.useMemo(
    () => buildColumns(product, onNavigate),
    [product, onNavigate],
  )
  const rows = React.useMemo(() => toSortableRows(terms), [terms])
  const previewActive = Boolean(embeddedPreview && !embeddedExpanded)
  const showEmbeddedFooter = previewActive && rows.length > embeddedPreviewRowLimit

  return (
    <DataTable<AllTermRow>
      data={rows}
      columns={columns}
      getRowId={row => row.id}
      selectable={false}
      searchable={!previewActive}
      showQueryControls={!previewActive}
      edgeInset={false}
      paginationOverride={
        previewActive ? { page: 1, pageSize: embeddedPreviewRowLimit } : undefined
      }
      hasFooter={showEmbeddedFooter}
      stickyFooter={
        showEmbeddedFooter ? (
          <TableViewMoreFooter
            totalCount={rows.length}
            visibleCount={embeddedPreviewRowLimit}
            edgeInset={false}
            viewMoreLabel={embeddedPreviewViewMoreLabel}
            onViewMore={() => {
              if (onEmbeddedPreviewViewMore) {
                onEmbeddedPreviewViewMore()
                return
              }
              setEmbeddedExpanded(true)
            }}
            className="rounded-none border-x-0 border-b-0"
          />
        ) : undefined
      }
      emptyState="No terms configured."
    />
  )
}
